import { Component, Element, Host, h, Prop } from '@stencil/core';
import { StringUtils } from '../../core/utils/StringUtils';
import { WebUtils } from '../../core/utils/WebUtils';

@Component({
  tag: 'snt-icon-copy',
  styleUrl: 'icon-copy-component.scss',
  shadow: false
})
export class IconCopyComponent {
  @Element() el: HTMLElement;
  @Prop({reflect: true}) titleString:string = '';
  @Prop({reflect: true}) styleClasses: string = StringUtils.empty();
  @Prop() propertiesSrc?:string;
  @Prop() properties?:string;
  @Prop({reflect: true}) resizeEl: HTMLElement;
  @Prop() logoIsSet: boolean = false;

  @Prop() primaryButtonText: string = StringUtils.empty();
  @Prop() primaryButtonAction: string = StringUtils.empty();
  @Prop() primaryButtonData: string = StringUtils.empty();
  @Prop() secondaryButtonText: string = StringUtils.empty();
  @Prop() secondaryButtonAction: string = StringUtils.empty();
  @Prop() secondaryButtonData: string = StringUtils.empty();
  @Prop() hyperlinkText: string = StringUtils.empty();
  @Prop() ctaUrl: string = StringUtils.empty();
  @Prop() openLinkInNewWindow: boolean = false;

  @Prop() linkHref:string;
  @Prop() linkTitle:string;
  @Prop() linkTarget:string;
  @Prop() linkRel:string;
  @Prop() linkActionReference:string;
  @Prop() linkActionData:string;
  @Prop() linkId: string = StringUtils.empty();

  private smallLogoUrl:string;
  private mediumLogoUrl:string;
  private largeLogoUrl:string;
  private extraLargeLogoUrl:string;

  private smallIconUrl:string;
  private largeIconUrl:string;

  public static readonly TAG_NAME:string = "snt-icon-copy";
  data: HTMLSntDataElement;
  
  private breakpoints = {
    xs: "0",
    sm: "576px",
    md: "768px",
    lg: "1024px",
    xl: "1440px",
    beyond: "1441px"
  }

  componentWillLoad() {
    const imgs = JSON.parse(this.properties);

    // Logo Image
    this.smallLogoUrl = imgs.smallLogoUrl || imgs.mediumLogoUrl || imgs.largeLogoUrl || imgs.extraLargeLogoUrl;
    this.mediumLogoUrl = imgs.mediumLogoUrl || imgs.largeLogoUrl || imgs.extraLargeLogoUrl || imgs.smallLogoUrl;
    this.largeLogoUrl = imgs.largeLogoUrl || imgs.extraLargeLogoUrl || imgs.mediumLogoUrl || imgs.smallLogoUrl;
    this.extraLargeLogoUrl = imgs.extraLargeLogoUrl || imgs.largeLogoUrl || imgs.mediumLogoUrl || imgs.smallLogoUrl;

    // Icon Image
    this.smallIconUrl = imgs.smallIconUrl || imgs.largeIconUrl;
    this.largeIconUrl = imgs.largeIconUrl || imgs.smallIconUrl;
  }

  render() {
    const logoContent = 
    <div class="logo">
      <picture>
        <source media={`(max-width: ${this.breakpoints.md})`} srcSet={this.smallLogoUrl}></source>
        <source media={`(max-width: ${this.breakpoints.lg})`} srcSet={this.mediumLogoUrl}></source>
        <source media={`(max-width: ${this.breakpoints.xl})`} srcSet={this.largeLogoUrl}></source>
        <source media={`(min-width: ${this.breakpoints.beyond})`} srcSet={this.extraLargeLogoUrl}></source>
        <img class="logo-image" loading="lazy" src={this.smallLogoUrl} alt="" />
      </picture>
    </div>

    const iconContent = <div class="icon">
      <picture>
        <source media={`(max-width: ${this.breakpoints.md})`} srcSet={this.smallIconUrl}></source>
        <source media={`(min-width: ${this.breakpoints.xl})`} srcSet={this.largeIconUrl}></source>
        <img class="icon-image" loading="lazy" src={this.smallIconUrl} alt="" />
      </picture>
    </div>

    const h3Content = this.el.hasAttribute('hasWrappedH3') ? <slot name="h3-slot" /> : <h3>{this.titleString}</h3>


    const primaryButtonContent: HTMLElement = this.buildActionButton(
      this.primaryButtonText,
      this.primaryButtonAction,
      this.primaryButtonData,
      "theme-default",
      this.ctaUrl,
      this.openLinkInNewWindow
    );
    const secondaryButtonContent: HTMLElement = this.buildActionButton(
      this.secondaryButtonText,
      this.secondaryButtonAction,
      this.secondaryButtonData,
      "theme-secondary",
      this.ctaUrl,
      this.openLinkInNewWindow
    );
    const linkContent: HTMLElement = this.buildLink(
      this.hyperlinkText,
      this.ctaUrl,
      "theme-link"
    );
    var isCTA = false;
    if(primaryButtonContent || secondaryButtonContent || linkContent){
      isCTA = true;
    }
    const iconCopyPanel = [
      <div class="icon-copy-panel">
        {this.logoIsSet ? logoContent : iconContent}
        {h3Content} 
        <div class={`${WebUtils.generateClasses(["content"], this.styleClasses)} ${isCTA ? "cta-margin" : ""}`}>
          <slot></slot>
        </div>
       {isCTA &&  <div id="cta" class="cta-container">
            {primaryButtonContent}
            {secondaryButtonContent}
            {linkContent}
        </div>
       }
      </div>
    ]
    
    return (
      <Host
        class={(this.linkHref || this.linkActionReference) ? "link" : null}>
        {(this.linkActionReference)
        ? <snt-action-link link-id={this.linkId} actionReference={this.linkActionReference} actionData={this.linkActionData}>{iconCopyPanel}</snt-action-link>
        : (this.linkHref)
        ? <snt-link link-id={this.linkId} href={this.linkHref} title={this.linkTitle} target={this.linkTarget} rel={this.linkRel}>{iconCopyPanel}</snt-link>
        : iconCopyPanel}
        <snt-data type="json" src={this.propertiesSrc} data={this.properties}  ref={(el) => this.data = el as HTMLSntDataElement} />
      </Host>
    );
  }
  buildActionButton(
    buttonText: string,
    buttonAction: string,
    buttonData: string,
    buttonClass: string,
    linkHref: string,
    openLinkInNewWindow: boolean
  ): HTMLElement {
    if(this.ctaUrl){
        return StringUtils.isEmpty(buttonText) ? null : (
          <button-link 
          class={`cta ${buttonClass} icon-copy-cta`}
          href={linkHref} 
          target={openLinkInNewWindow? "_blank" : "_self"} 
          rel-str="nofollow">{buttonText}
          </button-link>
        )
    }
    
    else{
    return StringUtils.isEmpty(buttonText) ? null : (
      <snt-action-button
        style-classes={`cta ${buttonClass}`}
        action-reference={buttonAction}
        action-data={buttonData}
        link-title={buttonText}
        class={"icon-copy-cta"}
      ></snt-action-button>
    )
    }
  }

  buildLink(
    linkTitle: string,
    linkUrl: string,
    linkClass: string
  ): HTMLElement {
    const linkContent = this.openLinkInNewWindow ? (
      linkTitle
    ) : (
      <span class="link-content">{linkTitle}</span>
    );

    return StringUtils.isEmpty(linkTitle) ? null : (
      <snt-link
        class-names={linkClass + " cta theme-action"}
        href={linkUrl}
        target={this.openLinkInNewWindow ? "_blank" : ""}
        class="icon-copy-cta"
      >
        {linkContent}
      </snt-link>
    );
  }
}
